import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
    Users, 
    Calendar, 
    Music, 
    Video, 
    Image,
    BarChart3,
    Shield,
    Activity,
    TrendingUp,
    Plus,
    Eye,
    Edit3,
    Trash2,
    UserCheck,
    UserX,
    Crown,
    Star,
    AlertCircle,
    CheckCircle,
    Clock,
    CreditCard,
    Mail,
    Bell
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';

const AdminDashboard = () => {
    const { user } = useAuth();
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [activeTab, setActiveTab] = useState('overview');

    useEffect(() => {
        fetchDashboardStats();
    }, []);

    const fetchDashboardStats = async () => {
        try {
            setLoading(true);
            setError(null);
            const response = await api.get('/admin/dashboard/stats');
            
            if (response.data.success) {
                setStats(response.data.data);
            } else {
                setError('Failed to load dashboard data');
            }
        } catch (err) {
            console.error('Error fetching admin stats:', err);
            setError('Unable to load dashboard. Please try again later.');
        } finally {
            setLoading(false);
        }
    };

    const formatNumber = (num) => {
        if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M';
        if (num >= 1000) return (num / 1000).toFixed(1) + 'K';
        return num?.toString() || '0';
    };

    const formatBytes = (bytes) => {
        if (!bytes) return '0 B';
        const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
        const i = Math.floor(Math.log(bytes) / Math.log(1024));
        return Math.round(bytes / Math.pow(1024, i) * 100) / 100 + ' ' + sizes[i];
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-gray-900 pt-20 pb-12">
                <div className="max-w-7xl mx-auto px-6">
                    <div className="glass-card p-8 text-center">
                        <Shield className="w-12 h-12 text-gray-600 mx-auto mb-3 animate-pulse" />
                        <p className="text-gray-400">Loading admin dashboard...</p>
                    </div>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="min-h-screen bg-gray-900 pt-20 pb-12">
                <div className="max-w-7xl mx-auto px-6">
                    <div className="glass-card p-8 text-center border border-red-500/30">
                        <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-3" />
                        <p className="text-red-400 mb-4">{error}</p>
                        <button 
                            onClick={fetchDashboardStats}
                            className="btn-secondary"
                        >
                            Try Again
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    const { overview, recent } = stats || {};

    const quickActions = [
        {
            title: 'Create Event',
            description: 'Add new ensemble events and showcases',
            icon: <Plus className="w-6 h-6" />,
            link: '/admin/events/create',
            color: 'bg-blue-600 hover:bg-blue-700'
        },
        {
            title: 'Manage Artists',
            description: 'View and manage ensemble members',
            icon: <Users className="w-6 h-6" />,
            link: '/admin/users',
            color: 'bg-blue-600 hover:bg-blue-700'
        },
        {
            title: 'Crew Management',
            description: 'Manage Deep Wave ensemble crew',
            icon: <Crown className="w-6 h-6" />,
            link: '/admin/crew',
            color: 'bg-yellow-600 hover:bg-yellow-700'
        },
        {
            title: 'Highlighted Artists',
            description: 'Manage featured artists showcase',
            icon: <Star className="w-6 h-6" />,
            link: '/admin/highlighted-artists',
            color: 'bg-blue-800 hover:bg-blue-900'
        },
        {
            title: 'Featured Songs',
            description: 'Manage homepage and dashboard featured songs',
            icon: <Music className="w-6 h-6" />,
            link: '/admin/featured-songs',
            color: 'bg-purple-600 hover:bg-purple-700'
        },
        {
            title: 'Event Calendar',
            description: 'Manage ensemble events and showcases',
            icon: <Calendar className="w-6 h-6" />,
            link: '/calendar',
            color: 'bg-green-600 hover:bg-green-700'
        },
        {
            title: 'Analytics Dashboard',
            description: 'Views, plays, likes, and visitor data',
            icon: <BarChart3 className="w-6 h-6" />,
            link: '/admin/analytics',
            color: 'bg-green-600 hover:bg-green-700'
        },
        {
            title: 'Analytics Settings',
            description: 'Configure Google Analytics tracking',
            icon: <BarChart3 className="w-6 h-6" />,
            link: '/admin/analytics/settings',
            color: 'bg-green-700 hover:bg-green-800'
        },
        {
            title: 'Storage',
            description: 'Disk usage by type, user, and file size',
            icon: <Activity className="w-6 h-6" />,
            link: '/admin/storage',
            color: 'bg-orange-600 hover:bg-orange-700'
        },
        {
            title: 'Site Settings',
            description: 'Configure site branding and header images',
            icon: <Image className="w-6 h-6" />,
            link: '/admin/settings',
            color: 'bg-blue-700 hover:bg-blue-800'
        },
        {
            title: 'Badge Management',
            description: 'Create and manage user achievement badges',
            icon: <Star className="w-6 h-6" />,
            link: '/admin/badges',
            color: 'bg-yellow-600 hover:bg-yellow-700'
        },
        {
            title: 'Manage Payments',
            description: 'Configure Square payments and subscription plans',
            icon: <CreditCard className="w-6 h-6" />,
            link: '/admin/payments',
            color: 'bg-green-600 hover:bg-green-700'
        },
        {
            title: 'Email Center',
            description: 'Send emails to individual users or bulk segments',
            icon: <Mail className="w-6 h-6" />,
            link: '/admin/email',
            color: 'bg-blue-600 hover:bg-blue-700'
        },
        {
            title: 'Band Message',
            description: 'Edit the message from the band on the homepage',
            icon: <Edit3 className="w-6 h-6" />,
            link: '/admin/band-message',
            color: 'bg-indigo-600 hover:bg-indigo-700'
        },
        {
            title: 'Notifications',
            description: 'Compose broadcasts and manage automatic notification types',
            icon: <Bell className="w-6 h-6" />,
            link: '/admin/notifications',
            color: 'bg-teal-600 hover:bg-teal-700'
        }
    ];

    return (
        <div className="min-h-screen bg-gray-900 pt-20 pb-12">
            <div className="max-w-7xl mx-auto px-6">
                {/* Header */}
                <div className="mb-8">
                    <div className="flex items-center gap-3 mb-2">
                        <Shield className="w-8 h-8 text-blue-400" />
                        <h1 className="text-4xl font-black gradient-text">Ensemble Management</h1>
                    </div>
                    <p className="text-gray-400">
                        Welcome back, <span className="text-blue-400 font-semibold">{user?.email}</span>
                    </p>
                </div>

                {/* Stats Overview */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
                    {/* Users Stats */}
                    <div className="glass-card p-6 border border-blue-500/30">
                        <div className="flex items-center justify-between mb-4">
                            <div className="p-3 bg-blue-600 rounded-lg">
                                <Users className="w-6 h-6 text-white" />
                            </div>
                            <TrendingUp className="w-5 h-5 text-green-400" />
                        </div>
                        <h3 className="text-2xl font-bold text-white mb-1">
                            {formatNumber(overview?.users?.total_users)}
                        </h3>
                        <p className="text-gray-400 text-sm mb-2">Ensemble Members</p>
                        <div className="flex justify-between text-xs">
                            <span className="text-blue-400">
                                {overview?.users?.verified_users} verified
                            </span>
                            <span className="text-green-400">
                                +{overview?.users?.new_today} today
                            </span>
                        </div>
                    </div>

                    {/* Events Stats */}
                    <div className="glass-card p-6 border border-blue-600/30">
                        <div className="flex items-center justify-between mb-4">
                            <div className="p-3 bg-blue-600 rounded-lg">
                                <Calendar className="w-6 h-6 text-white" />
                            </div>
                            <Activity className="w-5 h-5 text-blue-400" />
                        </div>
                        <h3 className="text-2xl font-bold text-white mb-1">
                            {formatNumber(overview?.events?.total_events)}
                        </h3>
                        <p className="text-gray-400 text-sm mb-2">Total Events</p>
                        <div className="flex justify-between text-xs">
                            <span className="text-blue-400">
                                {overview?.events?.upcoming_events} upcoming
                            </span>
                            <span className="text-yellow-400">
                                {overview?.events?.draft_events} drafts
                            </span>
                        </div>
                    </div>

                    {/* Uploads Stats */}
                    <div className="glass-card p-6 border border-green-500/30">
                        <div className="flex items-center justify-between mb-4">
                            <div className="p-3 bg-green-600 rounded-lg">
                                <Music className="w-6 h-6 text-white" />
                            </div>
                            <BarChart3 className="w-5 h-5 text-green-400" />
                        </div>
                        <h3 className="text-2xl font-bold text-white mb-1">
                            {formatNumber(overview?.uploads?.total_uploads)}
                        </h3>
                        <p className="text-gray-400 text-sm mb-2">Ensemble Works</p>
                        <div className="flex justify-between text-xs">
                            <span className="text-green-400">
                                {overview?.uploads?.audio_uploads} audio
                            </span>
                            <span className="text-blue-400">
                                {overview?.uploads?.video_uploads} video
                            </span>
                        </div>
                    </div>

                    {/* Storage Stats */}
                    <Link to="/admin/storage" className="glass-card p-6 border border-orange-500/30 hover:border-orange-400/50 transition-colors group block">
                        <div className="flex items-center justify-between mb-4">
                            <div className="p-3 bg-orange-600 rounded-lg group-hover:bg-orange-500 transition-colors">
                                <Activity className="w-6 h-6 text-white" />
                            </div>
                            <AlertCircle className="w-5 h-5 text-orange-400" />
                        </div>
                        <h3 className="text-2xl font-bold text-white mb-1">
                            {formatBytes(overview?.uploads?.total_storage_bytes)}
                        </h3>
                        <p className="text-gray-400 text-sm mb-2">Storage Used</p>
                        <div className="flex justify-between text-xs">
                            <span className="text-orange-400">
                                {formatBytes(overview?.uploads?.content_storage_bytes)} content
                            </span>
                            <span className="text-gray-500">
                                {formatBytes(overview?.uploads?.profile_storage_bytes)} profiles
                            </span>
                        </div>
                    </Link>
                </div>

                {/* Quick Actions */}
                <div className="mb-8">
                    <h2 className="text-2xl font-bold text-white mb-4">Quick Actions</h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                        {quickActions.map((action, index) => (
                            <Link
                                key={index}
                                to={action.link}
                                className={`glass-card p-6 hover:transform hover:scale-105 transition-all duration-300 group ${action.color}`}
                            >
                                <div className="flex flex-col items-center text-center">
                                    <div className="mb-3 group-hover:scale-110 transition-transform duration-300">
                                        {action.icon}
                                    </div>
                                    <h3 className="text-lg font-bold text-white mb-2">{action.title}</h3>
                                    <p className="text-sm text-gray-200 opacity-90">{action.description}</p>
                                </div>
                            </Link>
                        ))}
                    </div>
                </div>

                {/* Recent Activity */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                    {/* Recent Users */}
                    <div className="glass-card p-6">
                        <div className="flex items-center justify-between mb-6">
                            <h3 className="text-xl font-bold text-white flex items-center gap-2">
                                <Users className="w-5 h-5 text-blue-400" />
                                New Members
                            </h3>
                            <Link to="/admin/users" className="text-blue-400 hover:text-blue-300 text-sm">
                                View All →
                            </Link>
                        </div>
                        
                        <div className="space-y-3">
                            {recent?.users?.slice(0, 5).map((user) => (
                                <div key={user.id} className="flex items-center justify-between p-3 bg-gray-800/50 rounded-lg">
                                    <div className="flex items-center gap-3">
                                        <div className="w-8 h-8 bg-gradient-to-r from-blue-500 to-blue-700 rounded-full flex items-center justify-center">
                                            <span className="text-white text-sm font-bold">
                                                {(user.display_name || user.username || user.email).charAt(0).toUpperCase()}
                                            </span>
                                        </div>
                                        <div>
                                            <p className="text-white font-medium">
                                                {user.display_name || user.username || 'Unnamed User'}
                                            </p>
                                            <p className="text-gray-400 text-sm">{user.email}</p>
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        {user.is_admin && <Crown className="w-4 h-4 text-yellow-400" />}
                                        {user.is_verified ? (
                                            <CheckCircle className="w-4 h-4 text-green-400" />
                                        ) : (
                                            <Clock className="w-4 h-4 text-gray-400" />
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Recent Events */}
                    <div className="glass-card p-6">
                        <div className="flex items-center justify-between mb-6">
                            <h3 className="text-xl font-bold text-white flex items-center gap-2">
                                <Calendar className="w-5 h-5 text-blue-400" />
                                Upcoming Showcases
                            </h3>
                            <Link to="/calendar" className="text-blue-400 hover:text-blue-300 text-sm">
                                View All →
                            </Link>
                        </div>
                        
                        <div className="space-y-3">
                            {recent?.events?.slice(0, 5).map((event) => (
                                <div key={event.id} className="flex items-center justify-between p-3 bg-gray-800/50 rounded-lg">
                                    <div className="flex items-center gap-3">
                                        <div className="w-8 h-8 bg-gradient-to-r from-blue-600 to-blue-800 rounded-full flex items-center justify-center">
                                            <Calendar className="w-4 h-4 text-white" />
                                        </div>
                                        <div>
                                            <p className="text-white font-medium">{event.title}</p>
                                            <p className="text-gray-400 text-sm">
                                                {new Date(event.start_date).toLocaleDateString()} • {event.event_type}
                                            </p>
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <span className={`px-2 py-1 rounded-full text-xs ${
                                            event.status === 'published' 
                                                ? 'bg-green-600/20 text-green-400'
                                                : event.status === 'draft'
                                                ? 'bg-yellow-600/20 text-yellow-400'
                                                : 'bg-gray-600/20 text-gray-400'
                                        }`}>
                                            {event.status}
                                        </span>
                                        <Link 
                                            to={`/admin/events/${event.id}/edit`}
                                            className="p-1 hover:bg-white/10 rounded transition-colors"
                                        >
                                            <Edit3 className="w-4 h-4 text-gray-400 hover:text-white" />
                                        </Link>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                {/* System Health */}
                <div className="mt-8">
                    <div className="glass-card p-6">
                        <h3 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
                            <Activity className="w-5 h-5 text-green-400" />
                            System Health
                        </h3>
                        
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <div className="flex items-center gap-3 p-3 bg-green-600/20 rounded-lg">
                                <CheckCircle className="w-5 h-5 text-green-400" />
                                <div>
                                    <p className="text-green-400 font-semibold">API Status</p>
                                    <p className="text-gray-300 text-sm">All systems operational</p>
                                </div>
                            </div>
                            
                            <div className="flex items-center gap-3 p-3 bg-green-600/20 rounded-lg">
                                <CheckCircle className="w-5 h-5 text-green-400" />
                                <div>
                                    <p className="text-green-400 font-semibold">Database</p>
                                    <p className="text-gray-300 text-sm">Connected and healthy</p>
                                </div>
                            </div>
                            
                            <div className="flex items-center gap-3 p-3 bg-green-600/20 rounded-lg">
                                <CheckCircle className="w-5 h-5 text-green-400" />
                                <div>
                                    <p className="text-green-400 font-semibold">File Storage</p>
                                    <p className="text-gray-300 text-sm">Upload system active</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AdminDashboard;